import { ClipType, PolyFillType, } from "js-angusj-clipper";
import RBush from "rbush";
import { Path, Shape, ShapePath, Vector2, Vector3 } from "three"; // TODO: remove dep on Vector2 for frequent convesion
import { ensure } from "../common/ensure";
import { logShapeToConsole } from "../shapes/helpers/logShapeToConsole";
import { isClockwise } from "./helpers";
import polylabelFast from "./polylabel";
import { EndType, JoinType } from "./types";
// ClipperJS uses integers, so convert between floats and large ints.
// Per https://sourceforge.net/p/jsclipper/wiki/documentation/#clipperbaseaddpath
// coords should be between +/- 47M for speed (though
// https://github.com/xaviergonz/js-angusj-clipper/blob/master/docs/faq/index.md
// implies that might be much higher for the wasm version; untested). Flux's
// internal units are meters, so on the assumption that 40 meters is bigger than
// any PCB, 1M gives us plenty of headroom.
// TODO: move this into constants or models package
const CLIPPER_INT_SPACE_CONVERSION = 1_000_000;
function applyMatrix3ToClipperSpace(point, matrix) {
    const vector = new Vector2(point.x / CLIPPER_INT_SPACE_CONVERSION, point.y / CLIPPER_INT_SPACE_CONVERSION);
    vector.applyMatrix3(matrix);
    return {
        x: vector.x * CLIPPER_INT_SPACE_CONVERSION,
        y: vector.y * CLIPPER_INT_SPACE_CONVERSION,
    };
}
function applyMatrix4ToClipperSpace(point, matrix) {
    const vector = new Vector3(point.x / CLIPPER_INT_SPACE_CONVERSION, point.y / CLIPPER_INT_SPACE_CONVERSION, 0);
    vector.applyMatrix4(matrix);
    return {
        x: vector.x * CLIPPER_INT_SPACE_CONVERSION,
        y: vector.y * CLIPPER_INT_SPACE_CONVERSION,
    };
}
function fromClipperSpace(intSpace) {
    return intSpace / CLIPPER_INT_SPACE_CONVERSION;
}
function vectorFromClipperSpace(point) {
    return { x: point.x / CLIPPER_INT_SPACE_CONVERSION, y: point.y / CLIPPER_INT_SPACE_CONVERSION };
}
function vectorToClipperSpace(vector) {
    return {
        x: Math.round(vector.x * CLIPPER_INT_SPACE_CONVERSION),
        y: Math.round(vector.y * CLIPPER_INT_SPACE_CONVERSION),
    };
}
export class ClipperShape {
    clipper;
    // Based on https://github.com/Doodle3D/clipper-js but uses wasm-based clipper-lib and fixes
    // a few nits with Doodle3D/clipper-js
    paths = [];
    closed = true;
    // Lazily created
    rTree = null;
    /**
     * It's not recommended to use this constructor directly, because you'll need to deal with
     * the clipper library every time. Instead, use a factory.
     */
    constructor(clipper, polygons, closed = true) {
        this.clipper = clipper;
        this.paths = polygons.map((polygon) => {
            return polygon.map(vectorToClipperSpace);
        });
        this.closed = closed;
    }
    static fromClipperPaths(clipper, paths, closed = true) {
        const shape = new ClipperShape(clipper, [], closed);
        shape.paths = paths;
        return shape;
    }
    static fromObject(clipper, object) {
        const shape = new ClipperShape(clipper, [], object.closed);
        shape.paths = object.paths;
        return shape;
    }
    static boundsOverlap(bounds1, bounds2) {
        return !(bounds1.left > bounds2.right ||
            bounds1.right < bounds2.left ||
            bounds1.top > bounds2.bottom || // ClipperJS treats +y as down
            bounds1.bottom < bounds2.top);
    }
    static shapesIntersect(shape1, shape2) {
        // Performance optimization - cheaper, early cull
        if (!ClipperShape.boundsOverlap(shape1.shapeBounds(), shape2.shapeBounds())) {
            return false;
        }
        const intersection = shape2.intersect(shape1);
        const intersectArea = intersection.totalArea();
        return intersectArea !== 0;
    }
    toPolygons(opts = {}) {
        if (opts.outlinesFirst) {
            const outlines = this.paths.filter((path) => isClockwise(this.clipper, path));
            const holes = this.paths.filter((path) => !isClockwise(this.clipper, path));
            return [
                ...outlines.map((path) => path.map(vectorFromClipperSpace)),
                ...holes.map((path) => path.map(vectorFromClipperSpace)),
            ];
        }
        else {
            return this.paths.map((path) => path.map(vectorFromClipperSpace));
        }
    }
    toPolygon(index) {
        const path = this.paths.at(index);
        if (!path) {
            return [];
        }
        return path.map((point) => vectorFromClipperSpace(point));
    }
    /** Return true if the point is in the main polygon or any of the islands.
     * Note this implementation is different than the original in Doodle3d/clipper-js,
     * because the original pointInShape had a bug where it did not handle multiple islands
     * or nested islands correctly. This is what the even/odd count check fixes in
     * pointInSubPolys.
     */
    containsPoint(point) {
        let polys = 0;
        let holes = 0;
        const clipperPoint = vectorToClipperSpace(point);
        for (const path of this.paths) {
            if (this.clipper.pointInPolygon(clipperPoint, path) <= 0) {
                continue;
            }
            if (isClockwise(this.clipper, path)) {
                polys++;
            }
            else {
                holes++;
            }
        }
        // Important: just being in a hole poly does not mean it's in the hole, because it
        // could be in an island *inside* the hole. So check the even/odd count.
        return polys > 0 && polys % 2 !== holes % 2;
    }
    areVerticesContainedIn(shape) {
        const path = this.paths.at(0);
        if (!path) {
            return false;
        }
        return path.every((p) => shape.pointInSubPolysFast(p));
    }
    // eslint-disable-next-line @typescript-eslint/member-ordering
    searchBBCache = { minX: 0, minY: 0, maxX: 0, maxY: 0 };
    /** If point is in shape, return list of indexes of which polygons it's in.
     * TODO: change this method to private once legacy PCB connectivity graph removed, see FLUX-8073
     */
    pointInSubPolysFast(point) {
        if (this.rTree === null) {
            const boundingBoxes = this.paths.map((p, pathI) => ({
                minX: Math.min(...p.map((pp) => pp.x)),
                minY: Math.min(...p.map((pp) => pp.y)),
                maxX: Math.max(...p.map((pp) => pp.x)),
                maxY: Math.max(...p.map((pp) => pp.y)),
                pathI,
                orientation: isClockwise(this.clipper, p),
            }));
            this.rTree = new RBush();
            this.rTree.load(boundingBoxes);
        }
        this.searchBBCache.minX = point.x - 1;
        this.searchBBCache.minY = point.y - 1;
        this.searchBBCache.maxX = point.x + 1;
        this.searchBBCache.maxY = point.y + 1;
        const found = this.rTree.search(this.searchBBCache);
        if (found.length === 0) {
            return false;
        }
        const wasmClipper = this.clipper;
        const polys = found.filter(({ orientation, pathI }) => {
            if (!orientation) {
                return false;
            }
            const path = this.paths[pathI];
            if (!path) {
                return false;
            }
            return wasmClipper.pointInPolygon(point, path) === 1;
        });
        if (polys.length === 0) {
            return false;
        }
        const holes = found.filter(({ orientation, pathI }) => {
            if (orientation) {
                return false;
            }
            const path = this.paths[pathI];
            if (!path) {
                return false;
            }
            return wasmClipper.pointInPolygon(point, path) === 1;
        });
        // Important: just being in a hole poly does not mean it's in the hole, because it
        // could be in an island *inside* the hole. So check the even/odd count.
        return polys.length > 0 && polys.length % 2 !== holes.length % 2;
    }
    /** Convert a ClipperJS shape (with possible holes) to an array of 3js shapes.
     * Like ClipperJS, Three.ShapePath.toShapes() will treat clockwise/counterclockwise
     * paths as outlines/holes, so it maps conveniently. Unfortunately, 3js doesn't
     * seem to have a concept of a single shape made up of multiple polygons (except
     * for holes), hence the need to convert to multiple shapes.
     *
     * TODO: extract out or replace with a serialisation so that THREE less needed in this module
     */
    toThreeJSShapes() {
        const shape = new ShapePath();
        shape.subPaths = this.separateShapes().flatMap((shape) => shape.toPolygons().map((p) => new Path(p.map((pt) => new Vector2(pt.x, pt.y)))));
        return shape.toShapes(true);
    }
    toObject() {
        return {
            paths: this.paths,
            closed: this.closed,
        };
    }
    toShape(index) {
        return new Shape(this.toPolygon(index).map((point) => new Vector2(point.x, point.y)));
    }
    offset(delta, options = {}) {
        const result = this.clipper.offsetToPaths({
            delta: delta * CLIPPER_INT_SPACE_CONVERSION,
            arcTolerance: options.arcTolerance ?? 0.25,
            miterLimit: options.miterLimit ?? 2,
            offsetInputs: [
                {
                    data: this.paths,
                    joinType: options?.joinType ?? JoinType.Miter,
                    endType: options?.endType ?? EndType.ClosedPolygon,
                },
            ],
        });
        return ClipperShape.fromClipperPaths(this.clipper, ensure(result, `Failed to execute Clipper WASM offset(..)`));
    }
    /** Return true if shape has more than one non-hole polygon. */
    hasMultipleRegions() {
        return this.paths.filter((path) => isClockwise(this.clipper, path)).length > 1;
    }
    /*
     * Copied with minimal change from Doodle3D/clipper-js
     */
    separateShapes() {
        const shapes = [];
        if (!this.closed) {
            for (const path of this.paths) {
                shapes.push(ClipperShape.fromClipperPaths(this.clipper, [path], false));
            }
        }
        else {
            const areas = new WeakMap();
            const outlines = [];
            const holes = [];
            for (const path of this.paths) {
                const orientation = isClockwise(this.clipper, path);
                if (orientation) {
                    const area = this.clipper.area(path);
                    areas.set(path, area);
                    outlines.push(path);
                }
                else {
                    holes.push(path);
                }
            }
            outlines.sort((a, b) => areas.get(a) - areas.get(b));
            for (const outline of outlines) {
                const paths = [outline];
                for (const hole of [...holes]) {
                    const point = hole[0];
                    const pointInHole = this.clipper.pointInPolygon(point, outline) > 0;
                    if (pointInHole) {
                        paths.push(hole);
                        const index = holes.indexOf(hole);
                        // TODO: don't like this .. fix
                        holes.splice(index, 1);
                    }
                }
                shapes.push(ClipperShape.fromClipperPaths(this.clipper, paths, true));
            }
        }
        return shapes;
    }
    poleOfInaccesiblity(precision) {
        const polygons = [];
        const holes = [];
        for (const path of this.paths) {
            if (isClockwise(this.clipper, path)) {
                polygons.push(path);
            }
            else {
                holes.push(path);
            }
        }
        const position = polylabelFast([...polygons, ...holes], precision);
        const distance = fromClipperSpace(position.distance);
        const pos = vectorFromClipperSpace(position);
        return {
            pos,
            distance,
        };
    }
    /**
     * Note: it's important to ensure consistent, clockwise winding of input shapes,
     * for union of input shapes for union to behave in the expected way.
     *
     * Note: union with multiple shapes simultaneously is faster and scales *much* better
     * than chained individual union()s. Two eg docs I tested:
     *
     * Fill render time:    union() with multiple   chained union()
     * Doc A                35ms                    70ms
     * Doc B                60ms                    240ms
     */
    union(others) {
        return this.clip(others, {
            clipType: ClipType.Union,
            subjectFillType: PolyFillType.EvenOdd,
            // Specifying the clip fill-type as Positive here ensures that the multiple-union
            // has the expected result (otherwise combined shapes would end up subtracting and
            // doing other weird things in the intersections)
            clipFillType: PolyFillType.Positive,
        });
    }
    subtract(others) {
        switch (others.length) {
            case 0:
                return this;
            case 1:
                return this.clip(others, { clipType: ClipType.Difference });
            default:
                return this.clip([ClipperShape.fromClipperPaths(this.clipper, []).union(others)], {
                    clipType: ClipType.Difference,
                });
        }
    }
    intersect(other) {
        if (other.totalArea() === 0) {
            return this;
        }
        if (this.totalArea() === 0) {
            return this;
        }
        return this.clip([other], { clipType: ClipType.Intersection });
    }
    // WARNING: this returns values in int space
    shapeBounds() {
        const bounds = {
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
        };
        let initalized = false;
        for (const path of this.paths) {
            if (path.length === 0) {
                continue;
            }
            for (const point of path) {
                if (initalized) {
                    bounds.left = Math.min(bounds.left, point.x);
                    bounds.right = Math.max(bounds.right, point.x);
                    bounds.top = Math.min(bounds.top, point.y);
                    bounds.bottom = Math.max(bounds.bottom, point.y);
                }
                else {
                    bounds.left = point.x;
                    bounds.right = point.x;
                    bounds.top = point.y;
                    bounds.bottom = point.y;
                    initalized = true;
                }
            }
        }
        return bounds;
    }
    /**
     * Return bounds normalized from ClipperJS integer space and as a min/max Box2 as opposed to
     * a confusing left/right/top/bottom with inverted y
     */
    normalizedBounds() {
        const bounds = this.shapeBounds();
        return {
            min: { x: bounds.left / CLIPPER_INT_SPACE_CONVERSION, y: bounds.top / CLIPPER_INT_SPACE_CONVERSION },
            max: { x: bounds.right / CLIPPER_INT_SPACE_CONVERSION, y: bounds.bottom / CLIPPER_INT_SPACE_CONVERSION },
        };
    }
    normalizedBoundsRBush() {
        const bounds = this.shapeBounds();
        return {
            minX: bounds.left / CLIPPER_INT_SPACE_CONVERSION,
            minY: bounds.top / CLIPPER_INT_SPACE_CONVERSION,
            maxX: bounds.right / CLIPPER_INT_SPACE_CONVERSION,
            maxY: bounds.bottom / CLIPPER_INT_SPACE_CONVERSION,
        };
    }
    reverse() {
        this.clipper.reversePaths(this.paths);
        return this;
    }
    fixOrientation() {
        // TODO: add this to options of operations ? and remove method ?
        if (this.closed && this.totalArea() < 0) {
            this.reverse();
        }
        return this;
    }
    clean() {
        this.paths = this.clipper.cleanPolygons(this.paths);
    }
    logShapeToConsole(withMessage, fill, stroke) {
        logShapeToConsole(this.paths, withMessage, {
            fill: fill ?? "blue",
            stroke: stroke ?? "none",
            printSvgString: false,
        });
    }
    // Warning: mutates the shape
    transform3Shape(matrix) {
        this.paths = this.paths.map((path) => path.map((point) => applyMatrix3ToClipperSpace(point, matrix)));
        return this;
    }
    transform4Shape(matrix) {
        this.paths = this.paths.map((path) => path.map((point) => applyMatrix4ToClipperSpace(point, matrix)));
        return this;
    }
    transformShapeClone(matrix) {
        return ClipperShape.fromClipperPaths(this.clipper, this.paths.map((path) => path.map((point) => applyMatrix3ToClipperSpace(point, matrix))));
    }
    clone() {
        const clone = new ClipperShape(this.clipper, []);
        clone.paths = this.paths.map((path) => [...path]);
        clone.closed = this.closed;
        return clone;
    }
    isEmpty() {
        return this.paths.length === 0 || this.paths.every((p) => p.length === 0);
    }
    totalArea() {
        // Fast early exit
        if (this.isEmpty()) {
            return 0;
        }
        return (this.paths.reduce((totalArea, path) => totalArea + this.clipper.area(path), 0) /
            CLIPPER_INT_SPACE_CONVERSION ** 2);
    }
    clip(clips, options) {
        const allEmpty = !(this.paths.length || clips.flatMap((c) => c.paths).length);
        if (allEmpty) {
            // The underlying lib doesn't handle this degenerate case.
            return ClipperShape.fromClipperPaths(this.clipper, [], this.closed);
        }
        const result = this.clipper.clipToPaths({
            clipType: options.clipType,
            subjectInputs: [{ data: this.paths, closed: this.closed }],
            subjectFillType: options.subjectFillType ?? PolyFillType.EvenOdd,
            clipInputs: clips.map((s) => ({ data: s.paths, closed: s.closed })),
            clipFillType: options.clipFillType ?? PolyFillType.EvenOdd,
        });
        return ClipperShape.fromClipperPaths(this.clipper, ensure(result, `Failed to execute Clipper WASM clip(..)`));
    }
}
//# sourceMappingURL=ClipperShape.js.map